import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { MapPin, Mail, Send, ChevronRight, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Formspree Endpunkt – trage hier deine eigene Form-ID ein (z.B. https://formspree.io/f/xdoqpnrg)
const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_URL || 'https://formspree.io/f/mbdqzekg';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const contacts = [
  {
    group: 'Vorstand',
    items: [
      { name: 'Patrick Brüls', role: '1. Vorsitzender', email: 'patrick.bruels@hsc-alswede.de' },
      { name: 'Dennis Burkamp', role: '2. Vorsitzender', email: 'dennis.burkamp@hsc-alswede.de' },
      { name: 'Ulrich Haseloh', role: '3. Vorsitzender', email: 'ulrich.haseloh@hsc-alswede.de' },
    ],
  },
  {
    group: 'Fußball',
    items: [
      { name: 'Ulrich Haseloh', role: 'Fußballobmann', email: 'ulrich.haseloh@hsc-alswede.de' },
      { name: 'Sharon Nadine Brown', role: 'Fußballobfrau Frauen', email: 'sharon.nadine.brown@hsc-alswede.de' },
    ],
  },
  {
    group: 'Breitensport',
    items: [
      { name: 'Patrick Braun', role: 'Breitensportbeauftragter', email: 'breitensport@hsc-alswede.de' },
      { name: 'Claudia Brüls', role: 'Jugendkoordinatorin', email: 'claudia.bruels@hsc-alswede.de' },
    ],
  },
  {
    group: 'Allgemein & Presse',
    items: [
      { name: 'Presse', role: 'Presseanfragen', email: 'presse@hsc-alswede.de' },
      { name: 'Marcel Reinies', role: 'Verwaltung', email: 'marcel.reinies@hsc-alswede.de' },
    ],
  },
];

const sports = [
  'Fußball',
  'Fitness / Aerobic',
  'Tischtennis',
  'Volleyball',
  'Badminton',
  'Sportschießen',
  'Darts',
  'Kinderturnen / Mini-Kicker',
  'Krabbelgruppe',
  'Allgemein / Mitgliedschaft',
];

export default function Kontakt() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', sport: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = 'Kontakt | HSC Alswede – Mitglied werden & Probetraining';
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Bitte gib deinen Namen ein.';
    if (!form.email.trim()) errs.email = 'Bitte gib deine E-Mail-Adresse ein.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (!form.message.trim()) errs.message = 'Bitte gib eine Nachricht ein.';
    return errs;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || '',
          sport: form.sport || '',
          message: form.message,
        }),
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ name: '', email: '', phone: '', sport: '', message: '' });
        toast.success('Nachricht gesendet! Wir melden uns bald.', {
          description: 'Vielen Dank für deine Anfrage.',
        });
      } else {
        throw new Error('Server error');
      }
    } catch (err) {
      toast.error('Fehler beim Senden. Bitte versuche es erneut.', {
        description: 'Oder kontaktiere uns direkt per E-Mail.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'radial-gradient(circle at 70% 50%, #F5C200 0%, transparent 60%)'
        }} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-[#F5C200] text-sm mb-4">
              <Link to="/" className="text-gray-400 hover:text-gray-200 transition-colors">Start</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Kontakt</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Kontakt &amp; Mitglied werden
            </h1>
            <p className="text-gray-400 max-w-xl">
              Hast du Fragen oder möchtest du Mitglied werden? Schreib uns – wir freuen uns von dir zu hören!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <FadeIn>
                <div className="mb-8">
                  <div className="section-divider mb-3">
                    <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Kontaktformular</span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Schreib uns
                  </h2>
                  <p className="text-sm text-gray-500 mt-2">
                    Probetraining, Mitgliedschaft oder allgemeine Fragen – wir antworten schnell!
                  </p>
                </div>

                {success ? (
                  <div data-testid="contact-form-success-text" className="p-6 bg-green-50 rounded-2xl border border-green-200 text-center">
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
                    <h3 className="font-semibold text-green-800 mb-2">Nachricht gesendet!</h3>
                    <p className="text-sm text-green-600">Vielen Dank für deine Anfrage. Wir melden uns schnellstmöglich bei dir.</p>
                    <Button
                      onClick={() => setSuccess(false)}
                      className="mt-4 h-10 px-5 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors text-sm"
                    >
                      Weitere Nachricht senden
                    </Button>
                  </div>
                ) : (
                  <form
                    data-testid="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-[#1a1a1a]">
                          Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Dein vollständiger Name"
                          className="rounded-xl border-gray-200 h-11"
                        />
                        {errors.name && (
                          <p data-testid="contact-form-error-text" className="text-xs text-red-500">{errors.name}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-[#1a1a1a]">
                          E-Mail <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="deine@email.de"
                          className="rounded-xl border-gray-200 h-11"
                        />
                        {errors.email && (
                          <p data-testid="contact-form-error-text" className="text-xs text-red-500">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium text-[#1a1a1a]">
                          Telefon <span className="text-gray-400 font-normal">(optional)</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="0521 123456"
                          className="rounded-xl border-gray-200 h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sport" className="text-sm font-medium text-[#1a1a1a]">
                          Interesse an Sportart
                        </Label>
                        <select
                          id="sport"
                          name="sport"
                          value={form.sport}
                          onChange={handleChange}
                          className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]"
                        >
                          <option value="">Bitte auswählen...</option>
                          {sports.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-medium text-[#1a1a1a]">
                        Nachricht <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Schreib uns was du möchtest – Probetraining, Mitgliedschaft, Fragen..."
                        rows={5}
                        className="rounded-xl border-gray-200 resize-none"
                      />
                      {errors.message && (
                        <p data-testid="contact-form-error-text" className="text-xs text-red-500">{errors.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={loading}
                      className="w-full h-12 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors disabled:opacity-60"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-[#1a1a1a]/30 border-t-[#1a1a1a] rounded-full animate-spin" />
                          Wird gesendet...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Nachricht senden
                        </span>
                      )}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                    </p>
                  </form>
                )}
              </FadeIn>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-5">
              <FadeIn delay={0.15}>
                {/* Membership CTA Card */}
                <Card
                  data-testid="membership-cta-card"
                  className="rounded-2xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.06)] border-t-4 border-t-[#F5C200]"
                >
                  <h3 className="font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Mitglied werden
                  </h3>
                  <ul className="space-y-2 mb-5">
                    {[
                      'Kinder unter 6 kostenlos',
                      'Günstige Familienbeiträge',
                      'Alle Sportarten inklusive',
                      'Echte Gemeinschaft',
                    ].map((p, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#F5C200] flex-shrink-0" />
                        <span className="text-sm text-gray-700">{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid="membership-cta-join-button"
                    className="w-full h-11 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors"
                    onClick={() => {
                      const form = document.getElementById('name');
                      if (form) form.focus();
                    }}
                  >
                    Jetzt anmelden
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Card>

                {/* Address */}
                <Card className="rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.05)]">
                  <h3 className="font-semibold text-[#1a1a1a] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vereinsheim</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[#F5C200] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-[#1a1a1a]">Schanzeweg 7</div>
                        <div className="text-sm text-gray-500">32312 Lübbecke (Alswede)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-[#F5C200] flex-shrink-0" />
                      <a href="mailto:patrick.bruels@hsc-alswede.de" className="text-sm text-gray-600 hover:text-[#1a1a1a] transition-colors">
                        patrick.bruels@hsc-alswede.de
                      </a>
                    </div>
                  </div>
                </Card>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Persons */}
      <section className="py-12 lg:py-16 bg-[#FAFAF9]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="mb-8">
              <div className="section-divider mb-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Ansprechpartner</span>
              </div>
              <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Die richtigen Ansprechpartner
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contacts.map((group, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <Card className="rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.04)] h-full">
                  <div className="text-xs font-bold text-[#F5C200] uppercase tracking-wider mb-3">{group.group}</div>
                  <div className="space-y-3">
                    {group.items.map((item, j) => (
                      <div key={j}>
                        <div className="font-medium text-[#1a1a1a] text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500 mb-1">{item.role}</div>
                        <a href={`mailto:${item.email}`} className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors break-all">{item.email}</a>
                      </div>
                    ))}
                  </div>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}